import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { MentionNode } from './MentionNode';
import { MentionsPlugin } from './MentionsPlugin';
import { TagsToMentionsPlugin } from './TagsToMentionsPlugin';
import { $getRoot, $createParagraphNode, $createTextNode, $createLineBreakNode, EditorState, KEY_ENTER_COMMAND, COMMAND_PRIORITY_HIGH, SKIP_DOM_SELECTION_TAG } from 'lexical';
import { mergeRegister } from '@lexical/utils';

const MIN_INPUT_HEIGHT = 60;
const MAX_AUTO_INPUT_HEIGHT = 320;
const MAX_MANUAL_INPUT_HEIGHT = 560;
const RESERVED_WORKSPACE_HEIGHT = 260;

interface Document {
  id: string;
  title: string;
  type: string;
}

interface LexicalChatInputProps {
  documents: Document[];
  placeholder?: string;
  value?: string;
  onSubmit?: (message: string) => void;
  onChange?: (text: string) => void;
  disabled?: boolean;
  className?: string;
  onOpenDocumentModal?: () => void;
}

// Plugin to handle Enter key submission
function SubmitOnEnterPlugin({ onSubmit, controlled }: { onSubmit?: (message: string) => void; controlled: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event: KeyboardEvent) => {
          // Allow Shift+Enter for new line
          if (event?.shiftKey || event?.isComposing || editor.isComposing() || !editor.isEditable()) {
            return false;
          }

          // Prevent default and submit
          event?.preventDefault();

          // Get the text content
          const text = editor.getEditorState().read(() => {
            return $getRoot().getTextContent();
          });

          if (text.trim() && onSubmit) {
            onSubmit(text);

            // Clear editor after submit
            if (!controlled) editor.update(() => {
              const root = $getRoot();
              root.clear();
              const paragraph = $createParagraphNode();
              root.append(paragraph);
              paragraph.select();
            });
          }

          return true;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, onSubmit, controlled]);

  return null;
}

// Scroll sync not needed for simple chat input

function Editor({
  documents,
  value,
  placeholder,
  onSubmit,
  onChange,
  disabled,
  className,
  onOpenDocumentModal,
}: LexicalChatInputProps) {
  const [editor] = useLexicalComposerContext();
  const inputId = useId();
  const [manualHeight, setManualHeight] = useState<number | null>(null);
  const [inputHeight, setInputHeight] = useState(MIN_INPUT_HEIGHT);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const resizeStart = useRef<{ pointerId: number; clientY: number; height: number } | null>(null);
  const maximumHeight = Math.max(MIN_INPUT_HEIGHT, Math.min(MAX_MANUAL_INPUT_HEIGHT, Math.floor(viewportHeight * 0.6), viewportHeight - RESERVED_WORKSPACE_HEIGHT));
  const automaticMaximumHeight = Math.max(MIN_INPUT_HEIGHT, Math.min(MAX_AUTO_INPUT_HEIGHT, Math.floor(viewportHeight * 0.4), maximumHeight));
  const clampHeight = (height: number) => Math.max(MIN_INPUT_HEIGHT, Math.min(maximumHeight, Math.round(height)));

  useEffect(() => {
    const updateViewportHeight = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.target.getBoundingClientRect().height;
      if (height) setInputHeight(Math.round(height));
    });
    const removeRootListener = editor.registerRootListener((rootElement, previousRootElement) => {
      if (previousRootElement) observer.unobserve(previousRootElement);
      if (rootElement) observer.observe(rootElement);
    });
    return () => {
      removeRootListener();
      observer.disconnect();
    };
  }, [editor]);

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);
  useEffect(() => {
    if (value === undefined) return;
    const focused = editor.getRootElement()?.contains(document.activeElement);
    editor.update(() => {
      const root = $getRoot();
      if (root.getTextContent() === value) return;
      root.clear();
      const paragraph = $createParagraphNode();
      value.split('\n').forEach((line, index) => {
        if (index > 0) paragraph.append($createLineBreakNode());
        if (line) paragraph.append($createTextNode(line));
      });
      root.append(paragraph);
      if (focused) paragraph.selectEnd();
    }, { tag: focused ? ['external-input'] : ['external-input', SKIP_DOM_SELECTION_TAG] });
  }, [editor, value]);
  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent();
        onChange?.(text);
      });
    },
    [onChange]
  );

  return (
    <div className={`relative w-full min-w-0 ${className || ''}`}>
      <div className="relative h-4">
        <div
          role="separator"
          tabIndex={0}
          aria-label="Chat input height"
          aria-orientation="horizontal"
          aria-controls={inputId}
          aria-valuemin={MIN_INPUT_HEIGHT}
          aria-valuemax={maximumHeight}
          aria-valuenow={clampHeight(inputHeight)}
          aria-valuetext={`${manualHeight === null ? 'Automatic' : 'Manual'} height, ${clampHeight(inputHeight)} pixels`}
          title="Drag up to enlarge. Use Up/Down arrows to resize; double-click or press Enter for automatic height."
          className="group absolute inset-0 flex touch-none cursor-ns-resize select-none items-center justify-center rounded-t-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
          onPointerDown={event => {
            if (event.button !== 0 || !event.isPrimary) return;
            event.preventDefault();
            event.currentTarget.focus();
            resizeStart.current = {
              pointerId: event.pointerId,
              clientY: event.clientY,
              height: editor.getRootElement()?.getBoundingClientRect().height || inputHeight,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={event => {
            const start = resizeStart.current;
            if (!start || start.pointerId !== event.pointerId) return;
            setManualHeight(clampHeight(start.height + start.clientY - event.clientY));
          }}
          onPointerUp={event => {
            resizeStart.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => { resizeStart.current = null; }}
          onLostPointerCapture={() => { resizeStart.current = null; }}
          onDoubleClick={() => setManualHeight(null)}
          onKeyDown={event => {
            const height = editor.getRootElement()?.getBoundingClientRect().height || inputHeight;
            if (event.key === 'ArrowUp') setManualHeight(clampHeight(height + 24));
            else if (event.key === 'ArrowDown') setManualHeight(clampHeight(height - 24));
            else if (event.key === 'Home') setManualHeight(MIN_INPUT_HEIGHT);
            else if (event.key === 'End') setManualHeight(maximumHeight);
            else if (event.key === 'Enter') setManualHeight(null);
            else return;
            event.preventDefault();
          }}
        >
          <span className="h-1 w-10 rounded-full bg-slate-300 transition-colors group-hover:bg-slate-500 group-focus-visible:bg-blue-500" />
        </div>
        {manualHeight !== null && (
          <button
            type="button"
            onClick={() => setManualHeight(null)}
            className="absolute right-2 top-0 rounded px-1 text-[10px] leading-4 text-blue-700 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
            aria-label="Restore automatic input height"
            title="Fit the message automatically, up to the automatic height limit"
          >
            Auto
          </button>
        )}
      </div>
      <div className="relative">
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              id={inputId}
              className="w-full min-w-0 overflow-y-auto p-3 focus:outline-none"
              style={{
                fontSize: '0.875rem',
                lineHeight: '1.75rem',
                minHeight: MIN_INPUT_HEIGHT,
                height: manualHeight === null ? undefined : clampHeight(manualHeight),
                maxHeight: manualHeight === null ? automaticMaximumHeight : maximumHeight,
              }}
              aria-placeholder={placeholder}
              aria-label="Chat message"
              aria-describedby="chat-composer-help"
              data-chat-composer="true"
              placeholder={
                <div
                  className="absolute top-3 left-3 text-gray-400 pointer-events-none select-none"
                  style={{
                    fontSize: '0.875rem',
                    lineHeight: '1.75rem',
                  }}
                >
                  {placeholder || 'Type a message...'}
                </div>
              }
            />
          }
          placeholder={null}
          ErrorBoundary={({ children, onError }: { children: any; onError: (error: Error) => void }) => {
            return <>{children}</>;
          }}
        />
      </div>
      <OnChangePlugin onChange={handleChange} />
      <HistoryPlugin />
      <MentionsPlugin documents={documents} onOpenDocumentModal={onOpenDocumentModal} />
      <TagsToMentionsPlugin />
      <SubmitOnEnterPlugin onSubmit={onSubmit} controlled={value !== undefined} />
    </div>
  );
}

export function LexicalChatInput(props: LexicalChatInputProps) {
  const initialConfig = {
    namespace: 'ChatInput',
    theme: {
      paragraph: 'mb-1',
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
    },
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    nodes: [MentionNode],
    editable: !props.disabled,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="w-full min-w-0 bg-gray-50 rounded-lg border border-gray-200 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
        <Editor {...props} />
      </div>
    </LexicalComposer>
  );
}

// Hook to access editor from parent component
export function useLexicalEditor() {
  const [editor] = useLexicalComposerContext();

  const clearEditor = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      root.append(paragraph);
      paragraph.select();
    });
  }, [editor]);

  const setText = useCallback((text: string) => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(text));
      root.append(paragraph);
    });
  }, [editor]);

  const getText = useCallback(() => {
    return editor.getEditorState().read(() => {
      return $getRoot().getTextContent();
    });
  }, [editor]);

  return { clearEditor, setText, getText, editor };
}
